/**
 * AFAList Component
 * 
 * A comprehensive data table component for displaying and managing Alternative Fee Arrangements (AFAs).
 * This component is designed to be reusable across different contexts (organization, matter, etc.)
 * and provides full CRUD operations, filtering, searching, sorting, and export functionality.
 * 
 * Features:
 * - Generic component that works with different parent modules (organization, matter)
 * - Server-side pagination, sorting, and filtering capabilities
 * - Advanced search and filter functionality
 * - Export functionality with multiple export options (all, selected, current page)
 * - Responsive design with proper accessibility support
 * - Integration with Redux for access control
 * 
 * @author Tanjila Singh
 * @version 1.0.0
 */


// React core imports
import React, { useState, useEffect } from "react";

// React Router for navigation and URL parameter handling
import { useHistory } from "react-router-dom";

// Redux for state management
import { useDispatch, useSelector } from "react-redux";

// Custom table component with advanced features
import MuiDataTable from "../../../components/Table/MuiDataTable/MuiDataTable";

// UI components and notifications
import { useSnackbar } from "@livingdesign/react";

// AFA-specific constants and configuration
import {
	AFA_FAMILY,        // Available AFA family types
	AFA_STATUS,        // Available AFA status options
	AFA_TYPE,          // Available AFA types
	EXPORT_MAX_LIMIT_CONF,  // Export limitations
} from "../../../constants/constants";

// Table configuration constants
import {
	ROWS_PER_PAGE_DEFAULT,                    // Default pagination size
	ROWS_PER_PAGE_OPTIONS_ORGANIZATION,       // Pagination options
	ENABLE_SERVER_SIDE_FEATURES_ORGANIZATION  // Server-side features toggle
} from "../../../constants/tableConfigs";

// Modal components for export functionality
import ExportModal from "../../../components/Modal/ExportModal/ExportModal";

// Utility functions for error handling and file operations
import { getErrorMessage } from "../../../helpers/utils";

// Filter configuration constants
import { FILTER_TYPE } from "../../../constants/filterConfig";

// Table utility functions for actions and cell rendering
import { getAllowedActionsList, tableCellDisplay, formSearchQuery, getExportedData, exportClientData } from "../../../helpers/tableUtils";

// API service imports
import { getService, getServiceWithResponseType } from "../../../axios/axios";
import { Operations } from "../../../axios/operations";

// Redux actions for AFA state management
import { setOrganizationAfaData, setMatterAfaData } from "../../../store/actions/afaActions";

// Tab label constants for navigation
import { TAB_LABEL } from "../../../constants/tabLabel";

// Component-specific styles
import { AFAListStyle } from "./AFAList.styles";

// Color constants for status indicators
import { AFA_STATUS_TAG_COLORS } from "../../../constants/colors";
import { displayLocalDate } from "../../../helpers/utcUtils";


/**
 * Function to form search query from user entered search string
 *
 * @param {string} searchText
 * @param {Array} COLUMNS
 * @returns search query
 */
export const getSearchQuery = (searchText, COLUMNS) => {
	let query = "";
	COLUMNS?.forEach((column, index) => {
		if (column?.options?.display !== false && column?.operation) {
			const colQuery = formSearchQuery(column?.name, column?.operation, searchText, column?.type);
			if (colQuery !== "") {
				query += query ? " OR " + colQuery : colQuery;
			}
		}
	});
	return query;
};

/**
 * AFAList Component
 * 
 * A reusable component for displaying and managing Alternative Fee Arrangements (AFAs)
 * in a data table format. Supports multiple parent contexts (organization, matter, etc.)
 * 
 * @param {Object} props - Component props
 * @param {string} props.id - The ID of the parent entity (organization ID, matter ID, etc.)
 * @param {string} props.parentModule - The parent module context ('organization', 'matter', etc.)
 * 
 * @returns {JSX.Element} The rendered AFAList component
 */
const AFAList = ({ parentModule: propParentModule, id }) => {
	// Component styling using custom styles
	const classes = AFAListStyle();

	// Navigation hook for programmatic routing
	const history = useHistory();

	// Snackbar hook for user notifications (success, error messages)
	const { addSnack } = useSnackbar();

	// Redux hooks for state management
	const dispatch = useDispatch();

	/**
	 * Context Resolution
	 * 
	 * Determines the current context (parent module and ID) from either:
	 * 1. Props passed directly to the component (preferred)
	 * 2. URL parameters when used in routing context
	 * 
	 * This allows the component to work in both embedded and standalone contexts
	 */

	const currentParentModule = propParentModule;

	/**
	 * Filter Configuration
	 * 
	 * Defines the available filters for the AFA table following the CourtOrganizations pattern.
	 * Each filter configuration specifies:
	 * - colKey: The column key to filter on
	 * - type: The type of filter UI component to render
	 * - options: Available options for dropdown/checkbox filters
	 * - label: Display label for the filter
	 * - defaultOpen: Whether the filter should be expanded by default
	 * 
	 * Filter Types:
	 * - dropdownAutocomplete: Searchable dropdown with single selection
	 * - checkbox: Multi-select checkbox list
	 * - peopleDropdownAutocomplete: People picker with search functionality
	 */
	const FILTER_CONFIGS = [
		{
			colKey: "family",
			type: FILTER_TYPE.dropdownAutocomplete,
			options: AFA_FAMILY,
			label: "AFA Family",
			defaultOpen: true,
		},
		{
			colKey: "type",
			type: FILTER_TYPE.dropdownAutocomplete,
			options: AFA_TYPE,
			label: "AFA Type",
			defaultOpen: true,
		},
		{
			colKey: "status",
			label: "Status",
			type: FILTER_TYPE.checkbox,
			options: AFA_STATUS,
			defaultOpen: true,
		},
		{
			colKey: "ownerUserId",
			type: FILTER_TYPE.peopleDropdownAutocomplete,
			label: "Owner",
			defaultOpen: true,
		},
		{
			colKey: "createdBy",
			type: FILTER_TYPE.peopleDropdownAutocomplete,
			label: "Created by",
			defaultOpen: true,
		}
	];

	/**
	 * Column Definitions
	 * 
	 * Defines the structure and behavior of each column in the AFA data table.
	 * Following the NoteList pattern for API integration consistency.
	 * 
	 * Each column configuration includes:
	 * - name: The data property key to display
	 * - label: The column header text
	 * - operation: Search operation type for API queries
	 * - type: Data type for search query building
	 * - options: Column-specific configuration including:
	 *   - sort: Whether the column is sortable
	 *   - display: Whether the column is visible
	 *   - customBodyRender: Custom rendering function for cell content
	 *   - customFilterListOptions: Custom filter display formatting
	 */
	const COLUMNS = [
		{
			name: "name",
			label: "AFA Name",
			key: "name",
			options: {
				sort: true,
				display: true,
				customBodyRender: (value, tableMeta) => {
					return tableCellDisplay(`Mui-AFA-name-row-${value}`, value);
				},
				customFilterListOptions: { render: (v) => `AFA Name: ${v}` },
			},
			operation: Operations.LIKEIC,
			type: 'text'
		},
		{
			name: "family",
			label: "AFA Family",
			colKey: "family",
			options: {
				sort: true,
				display: true,
				filterList: [],
				customFilterListOptions: { render: (v) => `AFA Family: ${v}` },
			},
			operation: Operations.EQ,
			type: 'text'
		},
		{
			name: "type",
			label: "AFA Type",
			colKey: "type",
			options: {
				sort: true,
				display: true,
				filterList: [],
				customFilterListOptions: { render: (v) => `AFA Type: ${v}` },
			},
			operation: Operations.EQ,
			type: 'text'
		},
		{
			name: "status",
			label: "Status",
			colKey: "status",
			options: {
				sort: true,
				display: true,
				filterList: [],
				customBodyRender: (value) => {
					return (
						<span
							style={{
								backgroundColor: AFA_STATUS_TAG_COLORS[value]?.bgColor || "#9e9e9e",
								color: AFA_STATUS_TAG_COLORS[value]?.color || "white",
								padding: "4px 8px",
								borderRadius: "2px",
								fontSize: "12px",
							}}
						>
							{value}
						</span>
					);
				},
				customFilterListOptions: { render: (v) => `Status: ${v}` },
			},
			operation: Operations.EQ,
			type: 'text'
		},
		{
			name: "appliedTo",
			label: "Applied To",
			key: "applappliedToiedTo",
			options: {
				filter: false,
				sort: true,
				display: true,
			},
			operation: Operations.LIKEIC,
			type: 'text'
		},
		{
			name: "startDate",
			label: "Effective Start Date",
			key: "start Date",
			options: {
				filter: false,
				sort: true,
				display: true,
				customBodyRender: (value) => (value ? displayLocalDate(value) : "-")
			},
			operation: Operations.GTE,
			type: 'date'
		},
		{
			name: "ownerUserId",
			label: "Owner",
			colKey: "ownerUserId",
			options: {
				sort: true,
				display: true,
				filterList: [],
				customFilterListOptions: { render: (v) => `Owner: ${v}` },
			},
			operation: Operations.LIKEIC,
			type: 'tag'
		},
		{
			name: "createdBy",
			label: "Created By",
			colKey: "createdBy",
			options: {
				sort: true,
				display: false,
				filterList: [],
				customFilterListOptions: { render: (v) => `Created By: ${v}` },
			},
			operation: Operations.LIKEIC,
			type: 'tag'
		}
	];

	/**
	 * Redux State Management
	 * 
	 * Retrieves AFA data and user permissions from Redux store.
	 * Uses conditional path selection based on the parent module context:
	 * - Admin context: people.organization.details.tabs.afas
	 * - Matter context: matter.details.tabs.financials.tabs.afas
	 * 
	 * Note: useSelector is called unconditionally to avoid React Hooks rule violations
	 */
	const accessibilityState = useSelector((state) => state?.accessibility?.applicationAccess);
	const accessibility = accessibilityState?.organization?.tabs?.afas;

	// Get AFA data from Redux store based on context
	const afaDataFromStore = useSelector((state) => {
		return currentParentModule === "admin"
			? state?.afa?.organizationAfaData
			: state?.afa?.matterAfaData;
	});

	/**
	 * State Management
	 * 
	 * Following the NoteList pattern for API integration consistency.
	 * Each state variable serves a specific purpose in the table functionality:
	 * 
	 * Table Data & Configuration:
	 * - afaColumns: Column definitions for the data table
	 * - afaData: Current AFA data to display
	 * - loading: Loading state for data fetching operations
	 * - totalCount: Total number of records (for pagination)
	 * 
	 * Search & Filtering:
	 * - searchQuery: Current search query string for API
	 * - filterQuery: Current filter query string for API
	 * - sortQuery: Current sorting configuration for API
	 * 
	 * Pagination:
	 * - rowsPerPageAFA: Number of rows to display per page
	 * 
	 * Row Selection:
	 * - selectRowsData: Currently selected rows for bulk operations
	 * - rowsData: All row data for selection management
	 * 
	 * Export Functionality:
	 * - modalOpen: Controls export modal visibility
	 * - exportLoading: Loading state during export operations
	 * - exportButtonLoading: Loading state for export button
	 * - currentPageExport: Flag for current page export mode
	 * - exportSelection: Selected export scope (all, selected, currentPage)
	 */
	const [afaColumns, setAfaColumns] = useState(COLUMNS);
	const [loading, setLoading] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [filterQuery, setFilterQuery] = useState("");
	const [sortQuery, setSortQuery] = useState("&order=createdTs desc");
	const [rowsPerPageAFA, setRowsPerPageAFA] = useState(ROWS_PER_PAGE_DEFAULT);
	const [exportLoading, setExportLoading] = useState(false);
	const [exportButtonLoading, setExportButtonLoading] = useState(false);
	const [selectRowsData, setSelectRowsData] = useState([]);
	const [rowsData, setRowsData] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [currentPageExport, setCurrentPageExport] = useState(false);

	// Export configuration based on data size and limits
	const showExportAllOption = afaDataFromStore?.afas?.length < EXPORT_MAX_LIMIT_CONF.ORGANIZATION;
	const defaultExportOption = showExportAllOption ? 'allAFAs' : 'currentPage';
	const [exportSelection, setExportSelection] = useState(defaultExportOption);
	const { identifier } = useSelector((state) => state?.organization?.organizationDetailValues);
	let defaultFilterCondition = `${formSearchQuery(
		"isActive",
		Operations.EQ,
		true,
		'boolean'
	)}+${formSearchQuery(
		"organizationId",
		Operations.EQ,
		identifier,
		'id'
	)}`;

	const currentId = id;

	/**
	 * Data Fetching Effect
	 * 
	 * Automatically refetches AFA data when search, filter, or sort parameters change.
	 * This ensures the table stays synchronized with user interactions.
	 */
	useEffect(() => {
		let offsetQuery = "";
		if (ENABLE_SERVER_SIDE_FEATURES_ORGANIZATION) {
			offsetQuery = `&limit=${rowsPerPageAFA}&offset=0`;
		}
		if (currentParentModule === "matter") {
			offsetQuery = offsetQuery + `&type=Matter Level`
		}
		getAFAData(offsetQuery);
	}, [searchQuery, filterQuery, sortQuery]);

	/**
	 * Server-Side Event Handlers
	 * 
	 * These handlers manage server-side table features following the ExternalOrganizations pattern.
	 * Each handler updates the relevant state and resets pagination when needed.
	 */

	/**
	 * Handles pagination changes from the table component
	 * @param {Object} paginationData - Pagination configuration
	 * @param {number} paginationData.rowsPerPage - Number of rows to display per page
	 * @param {number} paginationData.page - Target page number (0-based)
	 */
	const handleServerSidePagination = ({ rowsPerPage, page }) => {
		setRowsPerPageAFA(rowsPerPage);
		getAFAData(`&limit=${rowsPerPage}&offset=${rowsPerPage * page}`);
	};

	/**
	 * Handles search input changes from the table component
	 * @param {string} searchText - Search query text
	 */
	const handleServerSideSearch = (searchText) => {
		const searchQuery = searchText
			? getSearchQuery(searchText, COLUMNS)
			: "";
		if (searchQuery !== "SHOW_NO_RESULT") {
			setSearchQuery(searchQuery);
		} else {
			// Clear Redux store data when no results
			if (currentParentModule === "admin") {
				dispatch(setOrganizationAfaData({ afas: [], count: 0 }));
			} else {
				dispatch(setMatterAfaData({ afas: [], count: 0 }));
			}
		}
	};

	/**
	 * Handles filter changes from the table component
	 * @param {string} query - Filter query string
	 */
	const handleServerSideFilter = (query) => {
		// Transform filter field names if needed (similar to NoteList pattern)
		if (query.indexOf("owner") !== -1) {
			query = query.replace("owner", "owner");
		}
		if (query.indexOf("createdBy") !== -1) {
			query = query.replace("createdBy", "createdBy");
		}
		setFilterQuery(query);
	};

	/**
	 * Handles sort changes from the table component
	 * @param {Object} sortConfig - Sort configuration
	 * @param {string} sortConfig.name - Column name to sort by
	 * @param {string} sortConfig.direction - Sort direction ('asc' or 'desc')
	 */
	const handleServerSideSort = ({ name, direction }) => {
		setSortQuery(`&order=${name} ${direction}`);
	};

	/**
	 * API Integration Function
	 * 
	 * Fetches AFA data from the backend API endpoint following the NoteList pattern.
	 * Constructs query parameters for search, filter, sort, and pagination.
	 * Updates Redux store with the fetched data.
	 * 
	 * @param {string} query - Optional pagination/limit query parameters
	 * 
	 * API Endpoint: /organizations/search/afas/v1
	 * Query Parameters:
	 * - filter: Combined search and filter query string
	 * - offset: Starting record number for pagination
	 * - limit: Number of records to return
	 * - order: Sort field and direction
	 * - csv: Boolean flag for CSV export format
	 */
	const getAFAData = (query = "") => {
		setLoading(true);

		if (currentParentModule === "matter") {
			defaultFilterCondition = defaultFilterCondition + ` ${formSearchQuery(
				"appliedToObjectId",
				Operations.LIKEIC,
				currentId,
				'id')}+${formSearchQuery(
					"type",
					Operations.LIKEIC,
					"Matter Level",
					'id'
				)}`
		}

		// Make API call to the AFA search endpoint
		getService(
			"ORGANIZATION_SERVICE", // Replace with actual service constant
			`/organizations/search/afas/v1?filter=(${defaultFilterCondition})${searchQuery !== '' ? 'and (' : ''}${searchQuery}${searchQuery !== '' ? ')' : ''} ${filterQuery}${sortQuery}${query}`
		)
			.then((response) => {
				// Update Redux store with API response data
				const afaData = response.data?.afas || response.data?.nodeList || [];
				const count = response.data?.count || 0;

				if (currentParentModule === "admin") {
					dispatch(setOrganizationAfaData({ afas: afaData, count }));
				} else {
					dispatch(setMatterAfaData({ afas: afaData, count }));
				}

				setLoading(false);
			})
			.catch((error) => {
				setLoading(false);
				addSnack({
					message: getErrorMessage(error)
				});
			});
	};

	/**
	 * Navigation Event Handlers
	 * 
	 * These handlers manage navigation to different AFA-related pages.
	 * They use the current parent module and ID to construct appropriate URLs.
	 */

	/**
	 * Navigates to the AFA details view page
	 * @param {Object} rowData - The AFA record data from the selected table row
	 */
	const handleRowClick = (rowData, rowMeta) => {
		const { id } = afaDataFromStore?.afas[rowMeta?.rowIndex];
		history.push(`/organization/afa/${id}`);
	};

	/**
	 * Row Selection Handler
	 * 
	 * Manages row selection state for bulk operations (like export).
	 * Called when users select/deselect rows in the table.
	 * 
	 * @param {Array} currentSelect - Currently selected row indices
	 * @param {Array} allSelected - All selected rows data
	 * @param {Array} selectRowsData - Selected rows metadata
	 */
	const handleRowSelectionChange = (currentSelect, allSelected, selectRowsData) => {
		const afaData = afaDataFromStore?.afas || [];
		const result = allSelected?.map(item => afaData && afaData[item?.index]);
		setSelectRowsData(selectRowsData);
		setRowsData(result);
	}

	/**
	 * Reset Selected Rows
	 * 
	 * Clears all row selections in the table. Used after bulk operations
	 * or when the user wants to start fresh with row selection.
	 */
	const resetSelectedRows = () => {
		setSelectRowsData([]);
		setRowsData([]);
	}

	/**
	 * Export Functionality
	 * 
	 * Handles the export of AFA data in various formats and scopes.
	 * Supports exporting all data, selected rows, or current page data.
	 */

	/**
	 * Opens the export modal for user to select export options
	 */
	const handleExport = () => {
		setExportSelection(defaultExportOption);
		setModalOpen(true);
		setExportButtonLoading(true);
	};

	/**
	 * Cancels the export operation and closes the export modal
	 */
	const onCancelExport = () => {
		setExportButtonLoading(false);
		setExportLoading(false);
		setModalOpen(false);
	};

	/**
	 * Main Export Function
	 * 
	 * Handles the actual export operation based on user's selection following the NoteList pattern.
	 * Supports three export modes:
	 * 1. 'currentSelection' - Export only selected rows
	 * 2. 'currentPage' - Export current page data
	 * 3. 'allAFAs' - Export all available data
	 * 
	 * Uses the same API endpoint with csv=true parameter for export functionality.
	 */
	const onClickExport = () => {
		setExportLoading(true);

		if (ENABLE_SERVER_SIDE_FEATURES_ORGANIZATION === false) {
			// Client-side export for development/testing
			exportClientData(afaDataFromStore?.afas, exportSelection, rowsData, 'afa-data');
			setModalOpen(false);
			setExportLoading(false);
		} else {
			// Server-side export using API
			let exportQuery = `filter=${searchQuery} ${filterQuery} ${sortQuery}`;

			if (exportSelection === 'currentSelection') {
				const result = rowsData.map(a => a.id);
				const innerQuery = formSearchQuery('id', Operations.IN, result, 'array') + '+' + formSearchQuery('organizationId', Operations.EQ, identifier, 'id');
				exportQuery = `filter=${encodeURIComponent(innerQuery)}`;
			} else if (exportSelection === 'currentPage') {
				const result = afaDataFromStore?.afas?.map(a => a.id);
				const innerQuery = formSearchQuery('id', Operations.IN, result, 'array') + '+' + formSearchQuery('organizationId', Operations.EQ, identifier, 'id');
				exportQuery = `filter=${encodeURIComponent(innerQuery)}`;
			}

			const csv = "csv=true";
			getServiceWithResponseType(
				"ORGANIZATION_SERVICE", // Replace with actual service constant
				`/organizations/search/afas/v1?${csv}&${exportQuery}`,
				"arraybuffer"
			)
				.then((response) => {
					const data = response?.data;
					const contentType = response?.headers["content-type"];
					getExportedData(data, contentType, 'afa-data');
					setModalOpen(false);
					setExportLoading(false);
					setExportButtonLoading(false);
				})
				.catch((error) => {
					setModalOpen(false);
					setExportLoading(false);
					setExportButtonLoading(false);
					addSnack({
						message: getErrorMessage(error)
					});
				});
		}
	};

	/**
	 * Table Configuration Options
	 * 
	 * Defines the configuration for the MuiDataTable component.
	 * Includes server-side features, pagination, filtering, and UI behavior settings.
	 * 
	 * Key Configuration:
	 * - filterConfigs: Filter definitions for the table
	 * - serverSide: Enables server-side processing for large datasets
	 * - Event handlers: Functions to handle server-side operations
	 * - Pagination: Current page, rows per page, and available options
	 * - UI settings: Filter type, responsiveness, and persistence
	 */
	const customOptions = {
		downloadOptions: {
			filename: "AFA_Export",
		},
		rowsPerPage: rowsPerPageAFA,
		rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS_ORGANIZATION,
		filterConfigs: FILTER_CONFIGS,
		serverSide: ENABLE_SERVER_SIDE_FEATURES_ORGANIZATION,
		onServerSideSearch: handleServerSideSearch,
		onServerSideFilter: handleServerSideFilter,
		onServerSidePagination: handleServerSidePagination,
		onServerSideSort: handleServerSideSort,
		totalCount: afaDataFromStore?.count || 0,
	}

	/**
	 * Component Render
	 * 
	 * Renders the complete AFAList component with:
	 * 1. MuiDataTable - Main data table with all AFA records
	 * 2. ExportModal - Modal for export functionality
	 * 
	 * The table includes:
	 * - Server-side pagination, search, filter, and sort capabilities
	 * - Row selection for bulk operations
	 * - Click handlers for navigation and actions
	 * - Loading states and accessibility controls
	 * - Export functionality with loading indicators
	 */
	return (
		<div className={classes.rowStyle} data-testid="afa-list-container">
			{/* Main AFA Data Table */}
			<MuiDataTable
				data-testid="afa-data-table"
				data={afaDataFromStore?.afas || []}                               // AFA records from Redux store
				columns={afaColumns}                                              // Column definitions
				setTableColumns={setAfaColumns}                                   // Column state setter
				customOptions={customOptions}                                     // Table configuration
				loading={loading}                                                 // Loading state
				onExportClick={() => handleExport()}                                      // Export button handler
				selectableRowsType={"multiple"}                                   // Enable multi-row selection
				selectedRows={selectRowsData}                                     // Currently selected rows
				onRowSelection={handleRowSelectionChange}                         // Row selection change handler
				showRowsCounter={false}                                           // Hide row counter
				exportLoading={exportLoading}                                     // Export loading state
				allowedActions={getAllowedActionsList(accessibility)}             // User permissions
				hideAddNewButton={true}
				resetSelectedRows={() => resetSelectedRows()}
				onRowClick={handleRowClick}
			/>

			{/* Export Modal Component */}
			<ExportModal
				data-testid="afa-export-modal"
				title={TAB_LABEL.AFAS}                          // Modal title
				mode={modalOpen}                                // Modal visibility state
				onCancel={onCancelExport}                       // Cancel export handler
				onCreate={onClickExport}                        // Execute export handler
				rowsData={rowsData}                             // Row data for export
				exportSelection={exportSelection}               // Current export selection
				setExportSelection={setExportSelection}         // Export selection setter
				currentPageExport={currentPageExport}           // Current page export flag
				showExportAllOption={showExportAllOption}       // Show "export all" option
				setCurrentPageExport={setCurrentPageExport}     // Current page export setter
			/>
		</div>
	);
};

export default AFAList;
